// SessionManager - Utility for managing session data in localStorage

const SessionManager = (function () {
  // Helper function to check if localStorage is available
  const isLocalStorageAvailable = () => {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  };

  // Helper function to safely get from localStorage
  const safeGetItem = (key) => {
    try {
      if (!isLocalStorageAvailable()) {
        return null;
      }
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`SessionManager: Error getting ${key}:`, error);
      return null;
    }
  };

  // Helper function to safely set to localStorage
  const safeSetItem = (key, value) => {
    try {
      if (!isLocalStorageAvailable()) {
        return false;
      }
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error(`SessionManager: Error setting ${key}:`, error);
      return false;
    }
  };

  // Create staff login session
  const createStaffLoginSession = (
    staff_id,
    staff_name,
    staff_phone,
    staff_email,
    password,
    staff_role,
    login_type,
    staff_image_path
  ) => {
    const staff = {
      isStaffLoggedIn: true,
      staff_id: staff_id,
      staff_name: staff_name,
      staff_phone: staff_phone,
      staff_email: staff_email,
      password: password,
      staff_role: staff_role,
      login_type: login_type,
      staff_image_path: staff_image_path,
    };

    const saved = safeSetItem('staff_details', JSON.stringify(staff));
    if (!saved) {
      console.error('SessionManager: Failed to save staff details');
    }
  };

  // Get staff details
  const getStaffDetails = () => {
    try {
      const staffData = safeGetItem('staff_details');
      if (!staffData) {
        return null;
      }
      return JSON.parse(staffData);
    } catch (error) {
      console.error('SessionManager: Error parsing staff details:', error);
      return null;
    }
  };

  // Save school code
  const saveSchoolCode = (schoolCode) => {
    try {
      // Validate that schoolCode is not null, undefined, or empty
      if (schoolCode == null || schoolCode === undefined || schoolCode === '') {
        console.error('SessionManager.saveSchoolCode: Invalid schoolCode value:', schoolCode);
        return false;
      }

      // Save to localStorage
      const saved = safeSetItem('SchoolCode', String(schoolCode));
      if (!saved) {
        // Fallback: try to save to sessionStorage as backup
        try {
          sessionStorage.setItem('SchoolCode', String(schoolCode));
          console.log('SessionManager.saveSchoolCode: Saved to sessionStorage as fallback');
          return true;
        } catch (sessionError) {
          console.error('SessionManager.saveSchoolCode: Failed to save to sessionStorage:', sessionError);
          return false;
        }
      }

      // Verify the value was saved correctly
      const savedValue = safeGetItem('SchoolCode');
      if (savedValue !== String(schoolCode)) {
        console.error('SessionManager.saveSchoolCode: Failed to verify saved value');
        return false;
      }

      console.log('SessionManager.saveSchoolCode: Successfully saved schoolCode:', schoolCode);
      return true;
    } catch (error) {
      console.error('SessionManager.saveSchoolCode: Error saving schoolCode:', error);
      return false;
    }
  };

  // Get school code
  const getSchoolCode = () => {
    // Primary: localStorage (via safeGetItem).
    const fromLocal = safeGetItem('SchoolCode');
    if (fromLocal != null && String(fromLocal).trim() !== '') {
      return fromLocal;
    }

    // Fallback: sessionStorage (used by saveSchoolCode when localStorage fails).
    try {
      const fromSession = sessionStorage.getItem('SchoolCode');
      if (fromSession != null && String(fromSession).trim() !== '') {
        return fromSession;
      }
    } catch (error) {
      console.error('SessionManager.getSchoolCode: Error reading sessionStorage:', error);
    }

    return null;
  };

  // Save current session
  const saveCurrentSession = (currentSession) => {
    if (currentSession == null) {
      console.error('SessionManager.saveCurrentSession: Invalid currentSession value');
      return false;
    }

    try {
      const saved = safeSetItem('CurrentSession', JSON.stringify(currentSession));
      if (!saved) {
        console.error('SessionManager: Failed to save current session');
        return false;
      }
      return true;
    } catch (error) {
      console.error('SessionManager.saveCurrentSession: Error saving session:', error);
      return false;
    }
  };

  // Get current session
  const getCurrentSession = () => {
    try {
      const sessionData = safeGetItem('CurrentSession');
      if (!sessionData) {
        return null;
      }
      return JSON.parse(sessionData);
    } catch (error) {
      console.error('SessionManager: Error parsing current session:', error);
      return null;
    }
  };

  return {
    createStaffLoginSession,
    getStaffDetails,
    saveSchoolCode,
    getSchoolCode,
    saveCurrentSession,
    getCurrentSession,
  };
})();

export default SessionManager;
